// @ts-strict

import { Component, Input, Output, OnInit, OnChanges, OnDestroy, ViewChildren, QueryList, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TrafficSourcesService } from '@services/traffic-sources/traffic-sources.service';
import { TextInputComponent, TextInputData } from '@app/shared/components/modal-inputs/text-input/text-input.component';
import { TextareaInputComponent, TextareaInputValueEmitter } from '@app/shared/components/modal-inputs/textarea-input/textarea-input.component';
import { NotificationTypes, NotificationType } from '@app/core/services/global-notifications/global-notifications.model';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TrafficSourceManagementService } from '@services/traffic-sources/traffic-source-management.service';
import { isArray, upperCase } from 'lodash';
import { CurrentModalSetting } from '../add-traffic-source-modal.component';
import { SelectContainerChosenValue } from '@app/shared/components/select-container/select-container.component';
import { ICreateTrafficSourceData } from '@app/core/services/traffic-sources/traffic-sources.types';
import { ICampaignConfigFieldData } from '@app/core/models/core-models';
import { CheckedCountries } from '@app/shared/components/countries-checkbox-list/countries-checkbox-list.component';
import { HttpErrorResponse } from '@angular/common/http';

export interface DetailsSectionState {
  isLoaded: boolean;
  summaryMode: boolean;
  token: null | string;
  traffic_source_id: null | number;
  setUrl?: string;
}

interface SelectData {
  id: number | string;
  name: string;
}
@Component({
  selector: 'app-details-section',
  templateUrl: './details-section.component.html',
  styleUrls: ['./details-section.component.scss']
})
export class DetailsSectionComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChildren(TextInputComponent) textInput!: QueryList<TextInputComponent>;
  @ViewChildren(TextareaInputComponent) textareaInput!: QueryList<TextareaInputComponent>;

  @Input() fieldsSetting?: CurrentModalSetting;

  @Output() setState: EventEmitter<DetailsSectionState> = new EventEmitter();

  public fieldsToSend: Record<string, any> = { traffic_source_id: null, additional_data: false };
  public isAdultCheckboxValue = false;
  public additionalInformation = false;
  public submitted = false;
  public categoriesArray?: ICampaignConfigFieldData;
  public countriesArray: string[] = [];
  public profilTypeSelect: { data: SelectData[]; title: string }[] = [];
  public currentDetailsMode = 'trafficSourceDetails';
  public addProfileTypeSelect = false;
  public fieldsInfo: ICampaignConfigFieldData[] = [];
  public selectContainers: { data: SelectData[]; title: string; required: boolean }[] = [];
  public countriesInitValues: string[] = [];
  public textInputConfigData: Record<string, { system_name: string; config_values: any }[]> = {};
  public submitBtnText = 'add_traffic_source';

  private modalFields: ICampaignConfigFieldData[] = [];
  private profileTypeFieldSets: ICreateTrafficSourceData[] = [];
  private detailsMode = {
    socialMedia: 'chooseSocialMedia',
    trafficSourceDetails: 'trafficSourceDetails'
  };
  private socialMediaFieldsSetting?: ICreateTrafficSourceData;
  private timeout?: NodeJS.Timeout;
  private typesOfFields: { system_name: string; type: string }[] = [];

  public get isSocialMedia(): boolean {
    return this.fieldsSetting?.id === 4;
  }

  private get isMobileApp(): boolean {
    return this.fieldsSetting?.id === 6;
  }

  private get isOtherSource(): boolean {
    return this.fieldsSetting?.id === 8;
  }

  public get isEditMode(): boolean {
    return !!this.fieldsSetting?.trafficSourceId;
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly trafficSourceService: TrafficSourcesService,
    private readonly globalNotificationsService: GlobalNotificationsService,
    public readonly activeModal: NgbActiveModal,
    private readonly trafficSourceManagementService: TrafficSourceManagementService
  ) {
    this.typesOfFields = this.trafficSourceService.addTrafficSourceTypesOfFields;
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.submitBtnText = 'save';
    }
  }

  ngOnChanges(): void {
    if (this.fieldsSetting !== null) {
      this.currentDetailsMode = this.isSocialMedia ? this.detailsMode.socialMedia : this.detailsMode.trafficSourceDetails;
      this.fieldsInfo = [];
      if (!this.isSocialMedia && this.fieldsSetting) {
        this.setModalFields(this.fieldsSetting.fields);
      }
    } else {
      this.clearData();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeout);
  }

  public setInputName(systemName: string): string {
    let inputName = systemName;
    if (inputName === 'url' && this.isMobileApp) {
      inputName = 'link_to_mobile_app';
    } else if (inputName === 'url' && this.isOtherSource) {
      inputName = 'link_to_service';
    }
    return inputName;
  }

  public onSelectSocialMedia(id: number | null): void {
    if (id !== null) {
      this.profilTypeSelect = [];
      this.addProfileTypeSelect = false;
      this.submitted = false;
      this.additionalInformation = false;
      this.currentDetailsMode = this.detailsMode.trafficSourceDetails;

      let socialMediaFieldsSetting = this.fieldsSetting?.children.find(el => el.id === id);

      if ((id === 9 || id === 18) && socialMediaFieldsSetting?.children) {
        this.addProfileTypeSelect = true;
        const newSelect = this.createSelects(socialMediaFieldsSetting.children);
        this.profilTypeSelect.push({ data: newSelect, title: 'profile_type' });
        this.profileTypeFieldSets = socialMediaFieldsSetting.children;
        socialMediaFieldsSetting = socialMediaFieldsSetting.children[0];
      }

      this.socialMediaFieldsSetting = socialMediaFieldsSetting;
      this.setModalFields(socialMediaFieldsSetting?.fields ?? []);
    } else {
      this.clearData();
    }
  }

  public onChangeProfileType($event: SelectContainerChosenValue<Record<string, any>>): void {
    this.submitted = false;

    const fieldSet = this.profileTypeFieldSets.find(el => el.id === $event.choosenValue.id);

    this.socialMediaFieldsSetting = fieldSet;
    this.setModalFields(fieldSet?.fields ?? []);
  }

  public onChangeIsAdultCheckboxValue(): void {
    this.isAdultCheckboxValue = !this.isAdultCheckboxValue;
    //@ts-ignore
    this.getValue({ system: 'is_adult', value: this.isAdultCheckboxValue.toString() }, 'checkbox');
  }

  public getAdditionalInfoValue(radioControlVal: boolean): void {
    this.getValue({ system: 'additional_data', value: radioControlVal }, 'additional_data');
    this.additionalInformation = radioControlVal;
  }

  public getCategoriesValue(categoriesArray: number[]): void {
    this.getValue({ system: 'categories', value: categoriesArray }, 'checkboxTree');
  }

  public getCheckedCountries(countries: CheckedCountries): void {
    const checkedCountries = countries.selectedCountries.length < 1 ? [] : [countries.selectedCountries.toString()];
    this.getValue({ system: 'destination_group_countries', value: checkedCountries }, 'checkboxList');
  }

  public getValue(
    event:
      | TextInputData
      | SelectContainerChosenValue<Record<string, any>>
      | TextareaInputValueEmitter
      | { system: string; value: boolean | number[] | string[] },
    fieldType: string
  ): void {
    if (fieldType === 'select' && 'title' in event) {
      this.fieldsToSend[event.title] = event.choosenValue.id;
    } else if ('system' in event) {
      this.fieldsToSend[event.system!] = event.value;
    }
  }

  public verifyForm(): void {
    this.submitted = true;
    let invalid = false;
    this.textInput.map(singleInput => {
      if (singleInput.configForm.text.invalid) {
        invalid = true;
        this.scrollTo('textInput');
        return;
      }
    });
    if (this.fieldsToSend.hasOwnProperty('categories') && this.fieldsToSend.categories.length < 1) {
      invalid = true;
      this.scrollTo('categories');
      return;
    }
    if (
      this.fieldsToSend.hasOwnProperty('destination_group_countries') &&
      this.checkIfRequired('destination_group_countries') &&
      this.fieldsToSend.destination_group_countries.length < 1
    ) {
      invalid = true;
      this.scrollTo('destinationGroupCountries');
      return;
    }
    this.textareaInput.map(singleInput => {
      if (singleInput.configForm.textarea.invalid) {
        invalid = true;
        this.scrollTo('textarea');
        return;
      }
    });
    if (!invalid) {
      if (this.isEditMode) {
        this.updateTrafficSource();
      } else {
        this.addTrafficSource();
      }
    }
  }

  public checkIfRequired(systemName: string): boolean {
    const field = this.fieldsInfo.find(el => el.system_name === systemName);
    return !!field?.required;
  }

  public checkValidators(systemName: string): string[] {
    const validators: string[] = [];
    switch (systemName) {
      case 'url':
        validators.push('url');
        break;
      case 'name':
      case 'ad_platform_other':
        validators.push('required');
        break;
      case 'views':
        validators.push('number');
        break;
      case 'followers':
      case 'subscriptions':
      case 'relations_number':
      case 'group_channel_number':
      case 'database_size':
      case 'download':
      case 'channel_number':
      case 'members_biggest_channel':
      case 'karma_number':
      case 'group_number':
      case 'members_biggest_group':
      case 'likes':
      case 'post_number':
      case 'friends':
      case 'people_connected':
      case 'members':
      case 'publishers':
        validators.push('number-required');
        break;
    }
    return validators;
  }

  private checkAdditionalFields(fieldSet: ICampaignConfigFieldData[]): boolean {
    const additionalField = fieldSet.find(el => el.required === false && el.system_name !== 'is_adult' && el.value !== null);
    return !!additionalField;
  }

  private getTypeOfField(fieldName: string): string {
    return this.typesOfFields.find(el => el.system_name === fieldName)?.type ?? '';
  }

  private setModalFields(fieldsSetting: ICampaignConfigFieldData[]): void {
    this.fieldsInfo = [];
    this.modalFields = fieldsSetting;
    const editMode = this.isEditMode;
    this.modalFields.map(field => {
      const fieldInfo = {
        system_name: field.system_name,
        type: this.getTypeOfField(field.system_name ?? ''),
        required: field.required,
        values: field.values
      };
      if (field.system_name && fieldInfo.type === 'select') {
        if (editMode) {
          this.fieldsToSend[field.system_name] = field.value !== null ? field.value : '0';
        } else {
          this.fieldsToSend[field.system_name] = '0';
        }
      } else if (field.system_name && fieldInfo.system_name === 'destination_group_countries') {
        if (field.value) {
          const countiesInitValues: string[] = field.value.split(',');
          this.fieldsToSend[field.system_name] = [...countiesInitValues];
          this.countriesInitValues = [...countiesInitValues];
        } else {
          this.fieldsToSend[field.system_name] = [];
        }
        if (field.values) {
          for (const [key, value] of Object.entries(field.values as never as Record<string, string | number>)) {
            this.countriesArray.push(key);
          }
        }
      } else if (fieldInfo.type === 'textInput' || fieldInfo.type === 'numberInput' || fieldInfo.type === 'textarea') {
        if (editMode && field.system_name) {
          this.fieldsToSend[field.system_name] = field.value !== null ? field.value : '';
          this.textInputConfigData[field.system_name] = [{ system_name: field.system_name, config_values: field?.value }];
        } else if (field.system_name) {
          this.fieldsToSend[field.system_name] = '';
          this.textInputConfigData[field.system_name] = [];
        }
      } else if (fieldInfo.system_name === 'is_adult' && editMode && field.value === '1') {
        this.isAdultCheckboxValue = true;
        this.fieldsToSend.is_adult = true;
      }
      this.fieldsInfo.push(fieldInfo);
      if (field.system_name === 'categories') {
        this.categoriesArray = field;
      }
    });
    if (editMode) {
      this.fieldsToSend.additional_data = this.checkAdditionalFields(fieldsSetting);
      if (this.fieldsToSend.additional_data) {
        this.getAdditionalInfoValue(true);
      }
    } else {
      // this.setAdditionalFieldsControl(fieldsSetting);
    }
    this.fieldsInfo.sort(this.sortRequiredFields);
    this.prepareSelects();
  }

  private clearData(): void {
    this.fieldsToSend = { traffic_source_id: null, additional_data: false };
    // this.showAdditionalFieldsControl = true;
    this.fieldsInfo = [];
    this.profilTypeSelect = [];
    this.profileTypeFieldSets = [];
    this.selectContainers = [];
    this.additionalInformation = false;
    this.isAdultCheckboxValue = false;
    this.submitted = false;
    this.categoriesArray = undefined;
    this.countriesArray = [];
  }

  private prepareSelects(): void {
    this.selectContainers = [];
    this.fieldsInfo.map(el => {
      if (el.type === 'select' && el.values && isArray(el.values)) {
        const newSelect = el.values.map(x => {
          const item = x as Record<string, string | number>;
          return { id: item.id, name: this.translate.instant('traffic_source_fields.' + item.lang) as string };
        });
        this.selectContainers.push({ data: newSelect, title: el.system_name ?? '', required: el.required ?? false });
      }
    });
  }

  private sortRequiredFields(a: ICampaignConfigFieldData, b: ICampaignConfigFieldData): number {
    return Number(b.required) - Number(a.required);
  }

  private createSelects(arrayOfElements: ICreateTrafficSourceData[]): SelectData[] {
    const newSelect = arrayOfElements.map(el => {
      return { id: el.id ?? 0, name: this.translate.instant('traffic_source_types.' + el.id) };
    });
    return newSelect;
  }

  private showNotification(message: string, type: (typeof NotificationTypes)[number], translate: boolean = true): void {
    this.globalNotificationsService.show({
      message: translate && !message.includes('view_translations.') ? `view_translations.${message}` : message,
      type,
      translate,
      isPersistent: false,
      id: ''
    });
  }

  private addTrafficSource(): void {
    this.fieldsToSend = { ...this.fieldsToSend, is_adult: this.isAdultCheckboxValue };

    if (this.fieldsSetting?.id) {
      this.fieldsToSend = { ...this.fieldsToSend, traffic_source_id: this.fieldsSetting?.id };
    }

    if (this.isSocialMedia && this.socialMediaFieldsSetting?.id) {
      this.fieldsToSend = { ...this.fieldsToSend, traffic_source_id: this.socialMediaFieldsSetting.id };
    }

    if (!this.fieldsToSend.views) {
      const { views, ...rest } = this.fieldsToSend;

      this.fieldsToSend = { ...rest };
    }

    this.setState.emit({ isLoaded: false, summaryMode: false, token: null, traffic_source_id: null });
    this.trafficSourceService.saveNewTrafficSource(this.fieldsToSend).subscribe(
      response => {
        this.showNotification('added_successfully', NotificationTypes[0]);

        if ([1, 2, 3].includes(this.fieldsToSend.traffic_source_id)) {
          this.trafficSourceService.getTrafficSourceVerificationToken(response.data?.traffic_source?.id ?? 0).subscribe(
            res => {
              this.setState.emit({
                isLoaded: true,
                summaryMode: true,
                token: res?.data?.verification?.guid ?? '',
                traffic_source_id: res?.data?.verification?.traffic_source_id ?? null,
                setUrl: this.fieldsToSend.url
              });
            },
            err => {
              if (err.error.errors.traffic_source_id[0] === 'no_auto_verify') {
                this.setState.emit({ isLoaded: true, summaryMode: true, token: null, traffic_source_id: null });
              }
              // this.showNotification(err.error.errors.traffic_source_id[0], NotificationTypes[1]);
            }
          );
        } else {
          this.trafficSourceManagementService.shareNewTrafficSourceStatus(true);
          this.activeModal.close();
          this.setState.emit({ isLoaded: true, summaryMode: false, token: null, traffic_source_id: null });
        }
      },
      (err: HttpErrorResponse) => {
        this.setState.emit({ isLoaded: true, summaryMode: false, token: null, traffic_source_id: null });
        if (err.error?.errors?.url) {
          this.textInput.first.backendError = true;
          this.scrollTo('textInput');
        }
        if (err.error?.errors?.message) {
          this.showNotification(err.error.errors.message, NotificationTypes[1], false);
        } else {
          if (err.error?.message) {
            this.showNotification(err.error.message, NotificationTypes[1], false);
          } else {
            if ((err.status === 422 || err.status === 403) && err.error) {
              Object.entries(err.error.errors as Record<string, string[]>).forEach(([key, value]) => {
                this.showNotification(upperCase(key) + ': ' + value, NotificationTypes[1], false);
                this.textInput.first.errorMsg = upperCase(key) + ': ' + value;
              });
            } else {
              this.showNotification('error_occured', NotificationTypes[1]);
            }
          }
        }
      }
    );
  }

  private scrollTo(elementId: string): void {
    const element = document.getElementById(elementId);
    this.timeout = setTimeout(() => {
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  private updateTrafficSource() {
    if (!this.fieldsSetting?.trafficSourceId) return;

    this.fieldsToSend.is_adult = this.isAdultCheckboxValue;
    this.setState.emit({ isLoaded: false, summaryMode: false, token: null, traffic_source_id: null });

    this.trafficSourceService.updateTrafficSource(this.fieldsSetting.trafficSourceId, this.fieldsToSend).subscribe(
      _res => {
        this.showNotification('edited_successfully', NotificationTypes[0]);
        this.trafficSourceManagementService.shareNewTrafficSourceStatus(true);
        this.activeModal.close();
        this.setState.emit({ isLoaded: true, summaryMode: false, token: null, traffic_source_id: null });
      },
      (err: HttpErrorResponse) => {
        console.log(err);
        this.setState.emit({ isLoaded: true, summaryMode: false, token: null, traffic_source_id: null });
        if (err.error?.errors?.url) {
          this.textInput.first.backendError = true;
          this.scrollTo('textInput');
        }
        if (err.error?.errors?.message) {
          this.showNotification(err.error.errors.message, NotificationTypes[1], false);
        } else {
          if (err.error?.message) {
            this.showNotification(err.error.message, NotificationTypes[1], false);
          } else {
            if ((err.status === 422 || err.status === 403) && err.error) {
              Object.entries(err.error.errors as Record<string, string[]>).forEach(([key, value]) => {
                this.showNotification(upperCase(key) + ': ' + value, NotificationTypes[1], false);
                this.textInput.first.errorMsg = upperCase(key) + ': ' + value;
              });
            } else {
              this.showNotification('error_occured', NotificationTypes[1]);
            }
          }
        }
      }
    );
  }
}
